import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material'
import {
  MenuBook,
  Edit,
  Delete,
  Search,
  Add,
  SmartToy
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const DefinicionesAdmin = () => {
  const { user } = useAuth()
  const [definiciones, setDefiniciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoria, setSelectedCategoria] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedDefinicion, setSelectedDefinicion] = useState(null)
  const [formData, setFormData] = useState({
    termino: '',
    definicion: '',
    categoria: 'general',
    activa: true
  })
  const [aiLoading, setAiLoading] = useState(false)
  const [aiAttempts, setAiAttempts] = useState(0)
  const [maxAiAttempts] = useState(10)

  // Generar clave única para los intentos de IA por sesión
  const sessionKey = `aiDefinitionAttempts_${formData.termino || 'new'}`

  // Cargar intentos existentes al cambiar el término
  React.useEffect(() => {
    if (formData.termino) {
      const savedAttempts = parseInt(sessionStorage.getItem(`aiDefinitionAttempts_${formData.termino}`) || '0')
      setAiAttempts(savedAttempts)
    } else {
      setAiAttempts(0)
    }
  }, [formData.termino])

  const categorias = [
    { value: 'general', label: 'General' },
    { value: 'calidad', label: 'Calidad' },
    { value: 'academico', label: 'Académico' },
    { value: 'administrativo', label: 'Administrativo' },
    { value: 'legal', label: 'Legal' },
    { value: 'tecnico', label: 'Técnico' },
    { value: 'NTCGP 1000:2009', label: 'NTCGP 1000:2009' }
  ]

  useEffect(() => {
    loadDefiniciones()
  }, [])

  const loadDefiniciones = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/definiciones', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDefiniciones(response.data)
    } catch (error) {
      setError('Error al cargar definiciones: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedDefinicion(null)
    setFormData({
      termino: '',
      definicion: '',
      categoria: 'general',
      activa: true
    })
    setAiAttempts(0) // Resetear intentos para nueva definición
    setOpenDialog(true)
  }

  const handleEdit = (definicion) => {
    setSelectedDefinicion(definicion)
    setFormData({
      termino: definicion.termino,
      definicion: definicion.definicion,
      categoria: definicion.categoria,
      activa: definicion.activa
    })
    // Los intentos se cargarán automáticamente por el useEffect cuando cambie formData.termino
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      
      if (selectedDefinicion) {
        // Editar
        await axios.put(`/api/definiciones/${selectedDefinicion.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Definición actualizada exitosamente')
      } else {
        // Crear
        await axios.post('/api/definiciones', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Definición creada exitosamente')
      }
      
      setOpenDialog(false)
      loadDefiniciones()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (definicion) => {
    if (!window.confirm(`¿Está seguro de eliminar la definición "${definicion.termino}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/definiciones/${definicion.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Definición eliminada exitosamente')
      loadDefiniciones()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  // Función para incrementar intentos de IA
  const incrementAiAttempts = (termino) => {
    const key = `aiDefinitionAttempts_${termino}`
    const newAttempts = aiAttempts + 1
    setAiAttempts(newAttempts)
    sessionStorage.setItem(key, newAttempts.toString())
  }

  const generateDefinitionWithAI = async () => {
    if (!formData.termino.trim()) {
      setError('Ingrese un término antes de generar la definición con IA')
      return
    }

    // Verificar límite de intentos
    if (aiAttempts >= maxAiAttempts) {
      setError(`Has alcanzado el límite de ${maxAiAttempts} intentos de generación con IA para el término "${formData.termino}" en esta sesión.`)
      return
    }

    // Incrementar contador de intentos
    incrementAiAttempts(formData.termino)

    setAiLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const prompt = `Proporciona una definición de glosario para "${formData.termino}" en el contexto de sistemas de gestión de calidad. La definición debe ser clara, precisa y técnicamente correcta, usando máximo 50 palabras. Responde únicamente con la definición, sin introducción ni explicaciones adicionales.`
      
      // Llamar al endpoint de generación de definiciones (vamos a crearlo)
      const response = await axios.post(
        '/api/definiciones/generate-with-ai',
        {
          termino: formData.termino,
          prompt: prompt
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      )

      if (response.data && response.data.definicion) {
        setFormData(prev => ({
          ...prev,
          definicion: response.data.definicion
        }))
        setSuccess('Definición generada con IA exitosamente. Puede editarla antes de guardar.')
      } else {
        setError('No se pudo generar la definición con IA')
      }
    } catch (error) {
      console.error('Error generando definición con IA:', error)
      setError('Error al generar definición: ' + (error.response?.data?.detail || error.message))
    } finally {
      setAiLoading(false)
    }
  }

  const getCategoriaLabel = (categoria) => {
    const cat = categorias.find(c => c.value === categoria)
    return cat ? cat.label : categoria
  }

  const getCategoriaColor = (categoria) => {
    switch (categoria) {
      case 'calidad': return 'error'
      case 'academico': return 'primary'
      case 'administrativo': return 'warning'
      case 'legal': return 'secondary'
      case 'tecnico': return 'info'
      default: return 'default'
    }
  }

  const filteredDefiniciones = definiciones.filter(def => {
    const matchesSearch = def.termino.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         def.definicion.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategoria = !selectedCategoria || def.categoria === selectedCategoria
    return matchesSearch && matchesCategoria
  })

  if (loading) {
    return (
      <Box>
        <Typography>Cargando definiciones...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Definiciones del Sistema
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nueva Definición
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar por término o definición..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={selectedCategoria}
              label="Categoría"
              onChange={(e) => setSelectedCategoria(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Término</TableCell>
              <TableCell>Definición</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDefiniciones.map((definicion) => (
              <TableRow key={definicion.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {definicion.termino}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {definicion.definicion}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getCategoriaLabel(definicion.categoria)}
                    color={getCategoriaColor(definicion.categoria)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={definicion.activa ? 'Activa' : 'Inactiva'}
                    color={definicion.activa ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(definicion)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                    {user?.rol === 'administrador' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(definicion)}
                        title="Eliminar"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredDefiniciones.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron definiciones
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDefinicion ? 'Editar Definición' : 'Nueva Definición'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Término"
              value={formData.termino}
              onChange={(e) => setFormData({ ...formData, termino: e.target.value })}
              required
              placeholder="Ej: SGC, Procedimiento, No Conformidad"
            />

            {/* Botón para generar definición con IA */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={aiLoading ? <CircularProgress size={16} /> : <SmartToy />}
                onClick={generateDefinitionWithAI}
                variant="outlined"
                color={aiAttempts >= maxAiAttempts ? "error" : "secondary"}
                disabled={aiLoading || !formData.termino.trim() || aiAttempts >= maxAiAttempts}
              >
                {aiLoading ? 'Generando...' : 
                 aiAttempts >= maxAiAttempts ? `Límite alcanzado (${aiAttempts}/${maxAiAttempts})` :
                 `🤖 Generar con IA (${aiAttempts}/${maxAiAttempts})`}
              </Button>
              {formData.termino.trim() && aiAttempts < maxAiAttempts && (
                <Typography variant="caption" color="text.secondary">
                  Generará definición de glosario para "{formData.termino}" (máx. 50 palabras)
                </Typography>
              )}
              {formData.termino.trim() && aiAttempts >= maxAiAttempts && (
                <Typography variant="caption" color="error">
                  Límite de {maxAiAttempts} intentos alcanzado para "{formData.termino}" en esta sesión
                </Typography>
              )}
            </Box>
            
            <TextField
              fullWidth
              label="Definición"
              multiline
              rows={4}
              value={formData.definicion}
              onChange={(e) => setFormData({ ...formData, definicion: e.target.value })}
              required
              placeholder="Definición completa y clara del término (o genérala con IA)"
              helperText={formData.definicion ? "Puede editar la definición generada antes de guardar" : ""}
            />
            
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.categoria}
                label="Categoría"
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              >
                {categorias.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activa}
                  onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
                />
              }
              label="Definición activa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.termino || !formData.definicion}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DefinicionesAdmin